import { type Dispatch, type SetStateAction, useEffect } from 'react';

import { useFanfics } from '@/hooks/use-fanfics';
import { buildDirectBackendUrl } from '@/lib/direct-backend-api';
import type { PublicAuthorSearchDoc, SearchResultItem } from '../types';
import { mapAuthorsToSearchResults } from '../utils/map-authors-to-search-results.util';

interface UseGlobalSearchParams {
  searchQuery: string;
  setSearchResults: Dispatch<SetStateAction<SearchResultItem[]>>;
  setIsSearching: Dispatch<SetStateAction<boolean>>;
  setShowDropdown: Dispatch<SetStateAction<boolean>>;
}

/** Debounced multi-source search: authors + works + articles. */
export function useGlobalSearch({
  searchQuery,
  setSearchResults,
  setIsSearching,
  setShowDropdown,
}: UseGlobalSearchParams): void {
  const { searchFanfics } = useFanfics();

  useEffect(() => {
    const searchAll = async () => {
      if (searchQuery.trim().length < 2) {
        setSearchResults([]);
        setShowDropdown(false);
        return;
      }

      setIsSearching(true);
      setShowDropdown(true);

      try {
        const query = searchQuery.trim();
        const [fanficsSettled, articlesSettled, authorsSettled] = await Promise.allSettled([
          searchFanfics(query),
          fetch(
            buildDirectBackendUrl(
              `/api/articles-custom/search?search=${encodeURIComponent(query)}`,
            ),
          ).then(async (response) => {
            if (!response.ok) {
              throw new Error(`Articles search failed: ${response.status}`);
            }
            return response.json() as Promise<{ docs?: SearchResultItem[] }>;
          }),
          fetch(
            buildDirectBackendUrl(
              `/api/users/search-public?q=${encodeURIComponent(query)}&limit=10`,
            ),
          ).then(async (response) => {
            if (!response.ok) {
              throw new Error(`Authors search failed: ${response.status}`);
            }
            return response.json() as Promise<{ docs?: PublicAuthorSearchDoc[] }>;
          }),
        ]);

        const authors =
          authorsSettled.status === 'fulfilled'
            ? mapAuthorsToSearchResults(authorsSettled.value.docs ?? [])
            : [];

        const articles =
          articlesSettled.status === 'fulfilled' ? (articlesSettled.value.docs ?? []) : [];

        const works =
          fanficsSettled.status === 'fulfilled'
            ? fanficsSettled.value.map((item) => ({
                ...item,
                title: item.name,
                type: 'fanfic' as const,
                workType: item.workType,
                rating: item.rating,
              }))
            : [];

        if (authorsSettled.status === 'rejected') {
          console.error('Authors search error:', authorsSettled.reason);
        }
        if (articlesSettled.status === 'rejected') {
          console.error('Articles search error:', articlesSettled.reason);
        }
        if (fanficsSettled.status === 'rejected') {
          console.error('Fanfics search error:', fanficsSettled.reason);
        }

        setSearchResults([...authors, ...works, ...articles]);
      } catch (error) {
        console.error('Search error:', error);
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    };

    const debounceTimer = setTimeout(searchAll, 300);
    return () => clearTimeout(debounceTimer);
  }, [searchQuery, searchFanfics, setSearchResults, setIsSearching, setShowDropdown]);
}
